import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { format, addDays, subDays, startOfDay } from 'date-fns';
import { ko } from 'date-fns/locale';
import { ChevronLeft, ChevronRight, Plus, Calendar as CalendarIcon, Trash2, CheckCircle, Edit2 } from 'lucide-react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { reservationsApi, Reservation, Room } from '../api/reservations';
import { platformApi, PlatformData, PlatformSummary, PlatformInventory, PlatformUpdate } from '../api/platform';
import LoadingSpinner from '../components/common/LoadingSpinner';
import clsx from 'clsx';

function getBusinessDate(): Date {
  const now = new Date();
  if (now.getHours() < 2) {
    return subDays(now, 1);
  }
  return now;
}

function formatTime(time: string | null | undefined): string {
  if (!time) return '-';
  // Handle ISO date string or time string
  if (time.includes('T')) {
    return time.split('T')[1].substring(0, 5);
  }
  return time.substring(0, 5);
}

function StatusBadge({ status }: { status: string }) {
  return (
    <span
      className={clsx(
        'inline-block px-2 py-0.5 text-xs font-semibold rounded',
        status === 'confirmed' && 'bg-green-100 text-green-800',
        status === 'cancelled' && 'bg-red-100 text-red-800',
        status === 'no_show' && 'bg-gray-100 text-gray-800'
      )}
    >
      {status === 'confirmed' ? '확정' : status === 'cancelled' ? '취소' : '노쇼'}
    </span>
  );
}

function StayBadge({ checkinDate, selectedDate }: { checkinDate: string; selectedDate: string }) {
  const isCheckInDay = checkinDate === selectedDate;
  return (
    <span
      className={clsx(
        'inline-block px-2 py-0.5 text-xs font-semibold rounded mr-1',
        isCheckInDay ? 'bg-green-500 text-white' : 'bg-blue-500 text-white'
      )}
    >
      {isCheckInDay ? '입실일' : '숙박중'}
    </span>
  );
}

function RoomTypeBadge({ roomType, count }: { roomType: string; count?: number }) {
  const getTypeColor = (type: string): string => {
    switch (type.toLowerCase()) {
      case 'standard':
        return 'bg-emerald-600';
      case 'standard twin':
        return 'bg-orange-400';
      case 'deluxe':
        return 'bg-purple-500';
      case 'family':
        return 'bg-pink-500';
      case 'studio':
        return 'bg-blue-500';
      case 'suite':
        return 'bg-purple-800';
      default:
        return 'bg-gray-500';
    }
  };

  const displayText = count !== undefined
    ? `${roomType.charAt(0).toUpperCase() + roomType.slice(1)}: ${count}개`
    : roomType;

  return (
    <span className={clsx('inline-block px-2 py-0.5 text-xs font-semibold rounded text-white', getTypeColor(roomType))}>
      {displayText}
    </span>
  );
}

// 객실 타입 정렬 순서
const ROOM_TYPE_ORDER = ['deluxe', 'family', 'standard', 'standard twin', 'studio', 'suite'];

// 객실 타입명 매핑 (플랫폼 API 응답 → 내부 타입)
const normalizeRoomType = (name: string): string => {
  const lower = name.toLowerCase();
  if (lower.includes('deluxe') || lower.includes('디럭스')) return 'deluxe';
  if (lower.includes('family') || lower.includes('패밀리')) return 'family';
  if (lower.includes('standard twin') || lower.includes('스탠다드 트윈')) return 'standard twin';
  if (lower.includes('standard') || lower.includes('스탠다드')) return 'standard';
  if (lower.includes('studio') || lower.includes('스튜디오')) return 'studio';
  if (lower.includes('suite') || lower.includes('스위트')) return 'suite';
  return lower;
};

// 수량 테이블 데이터 타입
interface RoomTypeQuantity {
  name: string;
  yanolja: number;
  yeogi: number;
}

// 가격 테이블 데이터 타입
interface PriceItem {
  platform: 'yanolja' | 'yeogi';
  rulesetId: number;
  name: string;
  price: number;
  saleOpen: boolean;
}

interface RoomTypePrices {
  name: string;
  items: PriceItem[];
}

export default function DashboardPage() {
  const [selectedDate, setSelectedDate] = useState(getBusinessDate());
  const [selectedRow, setSelectedRow] = useState<string | null>(null);
  const [yanoljaData, setYanoljaData] = useState<PlatformData | null>(null);
  const [yeogiData, setYeogiData] = useState<PlatformData | null>(null);
  const [isPlatformLoading, setIsPlatformLoading] = useState(false);
  const [platformError, setPlatformError] = useState<string | null>(null);
  const [isQuantityEditMode, setIsQuantityEditMode] = useState(false);
  const [isPriceEditMode, setIsPriceEditMode] = useState(false);
  const [editingQuantity, setEditingQuantity] = useState<{ platform: 'yanolja' | 'yeogi'; roomType: string } | null>(null);
  const [editingPrice, setEditingPrice] = useState<{ platform: 'yanolja' | 'yeogi'; rulesetId: number; currentPrice: number; productName: string } | null>(null);
  const queryClient = useQueryClient();

  const dateStr = format(selectedDate, 'yyyy-MM-dd');
  const todayStr = format(getBusinessDate(), 'yyyy-MM-dd');
  const isToday = dateStr === todayStr;

  const { data: reservations, isLoading } = useQuery({
    queryKey: ['activeReservations', dateStr],
    queryFn: () => reservationsApi.getActiveReservations(dateStr),
  });

  const { data: availableRooms, isLoading: isLoadingAvailable } = useQuery({
    queryKey: ['availableRooms', dateStr],
    queryFn: () => reservationsApi.getAvailableRooms(dateStr),
  });

  const { data: cancelledReservations, isLoading: isLoadingCancelled } = useQuery({
    queryKey: ['cancelledReservations', dateStr],
    queryFn: () => reservationsApi.getCancelledReservations(dateStr),
  });

  // 플랫폼 데이터 로드 (어제 이후 날짜만)
  const yesterday = startOfDay(subDays(new Date(), 1));
  const shouldShowPlatform = startOfDay(selectedDate) >= yesterday;

  useEffect(() => {
    let isMounted = true;

    if (!shouldShowPlatform) {
      setYanoljaData(null);
      setYeogiData(null);
      return;
    }

    const loadPlatformData = async () => {
      setIsPlatformLoading(true);
      setPlatformError(null);
      try {
        const [yanolja, yeogi] = await Promise.all([
          platformApi.getYanoljaData(dateStr),
          platformApi.getYeogiData(dateStr),
        ]);
        if (isMounted) {
          setYanoljaData(yanolja);
          setYeogiData(yeogi);
        }
      } catch (error: any) {
        console.error('플랫폼 데이터 로드 실패:', error);
        if (isMounted) {
          setPlatformError(error.message || '플랫폼 데이터를 불러올 수 없습니다');
        }
      } finally {
        if (isMounted) {
          setIsPlatformLoading(false);
        }
      }
    };

    loadPlatformData();
    return () => { isMounted = false; };
  }, [dateStr, shouldShowPlatform]);

  const clearVehiclesMutation = useMutation({
    mutationFn: reservationsApi.clearVehicles,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activeReservations', dateStr] });
    },
  });

  const cancelMutation = useMutation({
    mutationFn: (id: string) => reservationsApi.updateStatus(id, 'cancelled'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activeReservations', dateStr] });
      queryClient.invalidateQueries({ queryKey: ['cancelledReservations', dateStr] });
      queryClient.invalidateQueries({ queryKey: ['availableRooms', dateStr] });
    },
  });

  const activateMutation = useMutation({
    mutationFn: (id: string) => reservationsApi.updateStatus(id, 'confirmed'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activeReservations', dateStr] });
      queryClient.invalidateQueries({ queryKey: ['cancelledReservations', dateStr] });
      queryClient.invalidateQueries({ queryKey: ['availableRooms', dateStr] });
    },
  });

  const goToPreviousDay = () => setSelectedDate(subDays(selectedDate, 1));
  const goToNextDay = () => setSelectedDate(addDays(selectedDate, 1));
  const goToToday = () => setSelectedDate(getBusinessDate());

  const handleClearVehicles = (id: string) => {
    if (confirm('이 예약의 모든 차량 정보를 삭제하시겠습니까?')) {
      clearVehiclesMutation.mutate(id);
    }
  };

  const handleCancel = (id: string) => {
    if (confirm('예약을 취소하시겠습니까?')) {
      cancelMutation.mutate(id);
    }
  };

  const handleActivate = (id: string) => {
    if (confirm('이 예약을 활성화하시겠습니까?')) {
      activateMutation.mutate(id);
    }
  };

  // Count rooms by type
  const countByType = <T extends { roomType: string }>(items: T[] | undefined): Record<string, number> => {
    const counts: Record<string, number> = {};
    items?.forEach((item) => {
      const type = item.roomType.toLowerCase();
      counts[type] = (counts[type] || 0) + 1;
    });
    return counts;
  };

  const availableByType = countByType(availableRooms);
  const cancelledByType = countByType(cancelledReservations);

  // 플랫폼 데이터 처리 함수들
  const getQuantityData = (): Map<string, RoomTypeQuantity> => {
    const data = new Map<string, RoomTypeQuantity>();

    // 초기화
    ROOM_TYPE_ORDER.forEach(type => {
      data.set(type, { name: type, yanolja: 0, yeogi: 0 });
    });

    // 야놀자 데이터 처리
    if (yanoljaData?.data?.allSummaries) {
      yanoljaData.data.allSummaries.forEach((item: PlatformSummary) => {
        const type = normalizeRoomType(item.roomtypeName);
        const existing = data.get(type);
        if (existing) {
          existing.yanolja += item.reservableQuantity;
        }
      });
    }

    // 여기어때 데이터 처리
    if (yeogiData?.data?.allSummaries) {
      yeogiData.data.allSummaries.forEach((item: PlatformSummary) => {
        const type = normalizeRoomType(item.roomtypeName);
        const existing = data.get(type);
        if (existing) {
          existing.yeogi += item.reservableQuantity;
        }
      });
    }

    return data;
  };

  const getPriceData = (): Map<string, RoomTypePrices> => {
    const data = new Map<string, RoomTypePrices>();

    // 초기화
    ROOM_TYPE_ORDER.forEach(type => {
      data.set(type, { name: type, items: [] });
    });

    // 야놀자 가격 데이터
    if (yanoljaData?.data?.inventorySummaries) {
      yanoljaData.data.inventorySummaries.forEach((item: PlatformInventory) => {
        const type = normalizeRoomType(item.roomtypeName);
        const existing = data.get(type);
        if (existing) {
          existing.items.push({
            platform: 'yanolja',
            rulesetId: item.rulesetId,
            name: item.rulesetName,
            price: item.bookingRate,
            saleOpen: item.saleOpen,
          });
        }
      });
    }

    // 여기어때 가격 데이터
    if (yeogiData?.data?.inventorySummaries) {
      yeogiData.data.inventorySummaries.forEach((item: PlatformInventory) => {
        const type = normalizeRoomType(item.roomtypeName);
        const existing = data.get(type);
        if (existing) {
          existing.items.push({
            platform: 'yeogi',
            rulesetId: item.rulesetId,
            name: item.rulesetName,
            price: item.bookingRate,
            saleOpen: item.saleOpen,
          });
        }
      });
    }

    return data;
  };

  const quantityData = getQuantityData();
  const priceData = getPriceData();

  // 총 합계 계산
  const totalYanolja = Array.from(quantityData.values()).reduce((sum, item) => sum + item.yanolja, 0);
  const totalYeogi = Array.from(quantityData.values()).reduce((sum, item) => sum + item.yeogi, 0);
  const totalPlatform = totalYanolja + totalYeogi;

  // 중복 객실 체크
  const duplicateRoomIds = new Set<string>();
  const roomIdCounts: Record<string, number> = {};
  reservations?.forEach((r) => {
    if (r.roomId && r.roomId !== 'none') {
      roomIdCounts[r.roomId] = (roomIdCounts[r.roomId] || 0) + 1;
    }
  });
  Object.entries(roomIdCounts).forEach(([roomId, count]) => {
    if (count > 1) duplicateRoomIds.add(roomId);
  });

  const duplicateCount = duplicateRoomIds.size;
  const noneRoomCount = reservations?.filter((r) => r.roomId === 'none').length || 0;
  const turnoverWarningCount = reservations?.filter(
    (r) => r.turnoverTime && r.turnoverTime.totalMinutes <= 120
  ).length || 0;

  // 플랫폼 오버부킹 위험 체크 (플랫폼 합계 > 잔여 객실)
  const hasOverbookingRisk = ROOM_TYPE_ORDER.some((type) => {
    const qty = quantityData.get(type);
    if (!qty) return false;
    const total = qty.yanolja + qty.yeogi;
    const remaining = availableByType[type] || 0;
    return total > remaining;
  });

  return (
    <div className="space-y-4">
      {/* Date navigation */}
      <div className={clsx('card', isToday && 'bg-blue-50')}>
        <div className="flex items-center justify-center gap-2">
          <div className="flex items-center gap-1">
            <button onClick={goToPreviousDay} className="p-2 hover:bg-gray-100 rounded-lg">
              <ChevronLeft className="w-5 h-5" />
            </button>

            <CalendarIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
            <DatePicker
              selected={selectedDate}
              onChange={(date: Date | null) => date && setSelectedDate(date)}
              dateFormat="yyyy년 M월 d일 (EEEE)"
              locale={ko}
              customInput={
                <button className="text-lg font-semibold hover:text-blue-600 cursor-pointer">
                  {format(selectedDate, 'yyyy년 M월 d일 (EEEE)', { locale: ko })}
                </button>
              }
            />

            <button onClick={goToNextDay} className="p-2 hover:bg-gray-100 rounded-lg">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {isToday ? (
            <span className="px-2 py-0.5 text-xs font-semibold rounded bg-cyan-500 text-white whitespace-nowrap flex-shrink-0">
              오늘
            </span>
          ) : (
            <button onClick={goToToday} className="btn btn-outline text-sm py-1 whitespace-nowrap flex-shrink-0">
              오늘
            </button>
          )}
        </div>
      </div>

      {/* Reservations table */}
      <div className="card overflow-hidden p-0">
        <div className={clsx('px-4 py-3 border-b flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2', isToday ? 'bg-blue-100' : 'bg-gray-100')}>
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-lg font-bold text-gray-900">
              예약된 객실 현황 ({reservations?.length || 0})
            </h2>
            {duplicateCount > 0 && (
              <span className="px-2 py-0.5 text-xs font-semibold rounded bg-red-500 text-white">
                중복({duplicateCount})
              </span>
            )}
            {noneRoomCount > 0 && (
              <span className="px-2 py-0.5 text-xs font-semibold rounded bg-red-500 text-white">
                미배정({noneRoomCount})
              </span>
            )}
            {turnoverWarningCount > 0 && (
              <span className="px-2 py-0.5 text-xs font-semibold rounded bg-yellow-500 text-white">
                ⚠️ TOTW({turnoverWarningCount})
              </span>
            )}
            {shouldShowPlatform && hasOverbookingRisk && (
              <span className="px-2 py-0.5 text-xs font-semibold rounded bg-red-500 text-white">
                ⚠️ 오버부킹 위험
              </span>
            )}
          </div>
          <Link to="/reservations/new" className="btn btn-primary inline-flex items-center text-sm">
            <Plus className="w-4 h-4 mr-1" />
            새 예약
          </Link>
        </div>
        {isLoading ? (
          <LoadingSpinner className="py-8" />
        ) : reservations && reservations.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">#</th>
                  <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">관리</th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">예약번호</th>
                  <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">객실ID</th>
                  <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">객실호수</th>
                  <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">이름</th>
                  <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">박</th>
                  <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">차량</th>
                  <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">초기화</th>
                  <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">C/I Date</th>
                  <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">C/O Date</th>
                  <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">상태</th>
                  <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">C/I Time</th>
                  <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">C/O Time</th>
                  <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">L/CO</th>
                  <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">TO/Time</th>
                  <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">청소</th>
                  <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">Platform</th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">연락처</th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">체크인 특이사항</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {reservations.map((reservation: Reservation, index: number) => {
                  const checkinDateStr = reservation.checkinDate.split('T')[0];
                  const isCheckInDay = checkinDateStr === dateStr;
                  const isDuplicate = duplicateRoomIds.has(reservation.roomId);
                  const isNone = reservation.roomId === 'none';
                  const isSelected = selectedRow === reservation.reservationId;

                  const handleRowClick = () => {
                    setSelectedRow(isSelected ? null : reservation.reservationId);
                  };

                  return (
                    <tr
                      key={reservation.reservationId}
                      onClick={handleRowClick}
                      className={clsx(
                        'cursor-pointer transition-colors',
                        isSelected
                          ? 'bg-gray-600 text-white ring-2 ring-gray-800'
                          : index % 2 === 1
                          ? 'bg-gray-50 hover:bg-gray-100'
                          : 'hover:bg-gray-100'
                      )}
                    >
                      <td className="px-2 py-2 text-center whitespace-nowrap">{index + 1}</td>
                      <td className="px-2 py-2 text-center whitespace-nowrap">
                        <Link
                          to={`/reservations/${reservation.reservationId}`}
                          className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
                        >
                          관리
                        </Link>
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap">
                        <StayBadge checkinDate={checkinDateStr} selectedDate={dateStr} />
                        <span className="text-xs">{reservation.reservationId}</span>
                      </td>
                      <td
                        className={clsx(
                          'px-2 py-2 text-center whitespace-nowrap font-medium',
                          (isDuplicate || isNone) && 'bg-red-500 text-white'
                        )}
                      >
                        {isNone ? '배정실패' : reservation.roomId.toUpperCase()}
                      </td>
                      <td className="px-2 py-2 text-center whitespace-nowrap">
                        {isNone ? '미배정' : reservation.room?.roomAddress?.toUpperCase() || '-'}
                      </td>
                      <td className="px-2 py-2 text-center whitespace-nowrap">{reservation.guestName}</td>
                      <td className="px-2 py-2 text-center whitespace-nowrap">{reservation.nights}</td>
                      <td className="px-2 py-2 text-center whitespace-nowrap">
                        <RoomTypeBadge roomType={reservation.roomType} />
                      </td>
                      <td className="px-2 py-2 text-center text-xs whitespace-nowrap">
                        {reservation.guestParking && reservation.guestParking.length > 0 ? (
                          reservation.guestParking.map(p => p.carNumber).join(', ')
                        ) : '-'}
                      </td>
                      <td className="px-2 py-2 text-center whitespace-nowrap">
                        <button
                          onClick={() => handleClearVehicles(reservation.reservationId)}
                          className="px-2 py-1 text-xs bg-yellow-500 text-white rounded hover:bg-yellow-600"
                          disabled={clearVehiclesMutation.isPending}
                        >
                          차량삭제
                        </button>
                      </td>
                      <td className="px-2 py-2 text-center whitespace-nowrap text-xs">
                        {format(new Date(reservation.checkinDate), 'MM/dd')}
                      </td>
                      <td className="px-2 py-2 text-center whitespace-nowrap text-xs">
                        {format(new Date(reservation.checkoutDate), 'MM/dd')}
                      </td>
                      <td className="px-2 py-2 text-center whitespace-nowrap">
                        <button
                          onClick={() => handleCancel(reservation.reservationId)}
                          className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600"
                          disabled={cancelMutation.isPending}
                        >
                          예약취소
                        </button>
                      </td>
                      <td className="px-2 py-2 text-center whitespace-nowrap text-xs">
                        {formatTime(reservation.checkinTime)}
                      </td>
                      <td className="px-2 py-2 text-center whitespace-nowrap text-xs">
                        {formatTime(reservation.checkoutTime)}
                      </td>
                      <td className="px-2 py-2 text-center whitespace-nowrap">
                        {reservation.isLateCheckout ? '✅' : '❌'}
                      </td>
                      <td className={clsx(
                        'px-2 py-2 text-center whitespace-nowrap text-xs',
                        reservation.turnoverTime && reservation.turnoverTime.totalMinutes <= 120 && 'bg-yellow-100 text-yellow-800 font-medium'
                      )}>
                        {reservation.turnoverTime ? reservation.turnoverTime.formatted : '-'}
                      </td>
                      <td className="px-2 py-2 text-center whitespace-nowrap text-xs">
                        {reservation.cleaningStatus || '-'}
                      </td>
                      <td className="px-2 py-2 text-center whitespace-nowrap text-xs">{reservation.platform}</td>
                      <td className="px-2 py-2 whitespace-nowrap text-xs">{reservation.guestPhone || '-'}</td>
                      <td className="px-2 py-2 whitespace-nowrap text-xs">
                        {reservation.checkinMemo || '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">해당 날짜에 예약이 없습니다.</div>
        )}
      </div>

      {/* Platform Sales Section */}
      {shouldShowPlatform && (
        <div className="card overflow-hidden p-0">
          <div className={clsx('px-4 py-3 border-b', isToday ? 'bg-blue-100' : 'bg-gray-100')}>
            <h2 className="text-lg font-bold text-gray-900">플랫폼 판매현황</h2>
          </div>

          {isPlatformLoading ? (
            <LoadingSpinner className="py-8" />
          ) : platformError ? (
            <div className="text-center py-8 text-red-500">{platformError}</div>
          ) : (
            <div className="p-0 sm:p-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 sm:gap-4">
                {/* 판매중 객실 테이블 */}
                <div className="sm:border sm:rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-3 py-2 border-b flex items-center justify-between">
                    <h3 className="font-semibold text-gray-700">판매중 객실</h3>
                    <label className="flex items-center gap-1 cursor-pointer text-xs">
                      <span className="text-gray-500">수정</span>
                      <input
                        type="checkbox"
                        checked={isQuantityEditMode}
                        onChange={(e) => setIsQuantityEditMode(e.target.checked)}
                        className="w-3.5 h-3.5 rounded border-gray-300"
                      />
                    </label>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">객실타입</th>
                          <th className="px-3 py-2 text-center text-xs font-medium text-red-500 uppercase">야놀자</th>
                          <th className="px-3 py-2 text-center text-xs font-medium text-blue-500 uppercase">여기어때</th>
                          <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">합계</th>
                          <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">잔여</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {ROOM_TYPE_ORDER.map((type) => {
                          const qty = quantityData.get(type);
                          if (!qty) return null;
                          const total = qty.yanolja + qty.yeogi;
                          const remaining = availableByType[type] || 0;
                          const isOverbooking = total > remaining;
                          const needsCheck = remaining > total && remaining > 0;

                          return (
                            <tr
                              key={type}
                              className={clsx(
                                isOverbooking && 'bg-red-100',
                                needsCheck && 'bg-green-100'
                              )}
                            >
                              <td className="px-3 py-2 whitespace-nowrap">
                                <RoomTypeBadge roomType={type} />
                              </td>
                              <td
                                className={clsx(
                                  'px-3 py-2 text-center whitespace-nowrap font-medium text-red-600',
                                  isQuantityEditMode && 'cursor-pointer hover:bg-red-50'
                                )}
                                onClick={() => isQuantityEditMode && setEditingQuantity({ platform: 'yanolja', roomType: type })}
                              >
                                {qty.yanolja}
                                {isQuantityEditMode && <Edit2 className="w-3 h-3 inline ml-1 text-gray-400" />}
                              </td>
                              <td
                                className={clsx(
                                  'px-3 py-2 text-center whitespace-nowrap font-medium text-blue-600',
                                  isQuantityEditMode && 'cursor-pointer hover:bg-blue-50'
                                )}
                                onClick={() => isQuantityEditMode && setEditingQuantity({ platform: 'yeogi', roomType: type })}
                              >
                                {qty.yeogi}
                                {isQuantityEditMode && <Edit2 className="w-3 h-3 inline ml-1 text-gray-400" />}
                              </td>
                              <td className="px-3 py-2 text-center whitespace-nowrap font-bold">
                                {total}
                              </td>
                              <td className="px-3 py-2 text-center whitespace-nowrap font-medium">
                                {remaining}
                              </td>
                            </tr>
                          );
                        })}
                        {/* 합계 행 */}
                        <tr className="bg-gray-100 font-bold">
                          <td className="px-3 py-2 whitespace-nowrap">합계</td>
                          <td className="px-3 py-2 text-center whitespace-nowrap text-red-600">{totalYanolja}</td>
                          <td className="px-3 py-2 text-center whitespace-nowrap text-blue-600">{totalYeogi}</td>
                          <td className="px-3 py-2 text-center whitespace-nowrap">{totalPlatform}</td>
                          <td className="px-3 py-2 text-center whitespace-nowrap">{availableRooms?.length || 0}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  {/* 범례 - 모바일만 표시 */}
                  <div className="sm:hidden px-3 py-2 flex gap-3 text-[10px] text-gray-500 border-t bg-gray-50">
                    <span className="flex items-center gap-1">
                      <span className="w-2.5 h-2.5 bg-red-100 border border-red-200 rounded"></span>
                      오버부킹
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-2.5 h-2.5 bg-green-100 border border-green-200 rounded"></span>
                      수량체크
                    </span>
                  </div>
                </div>

                {/* 판매가격 테이블 */}
                <div className="sm:border sm:rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-3 py-2 border-b flex items-center justify-between">
                    <h3 className="font-semibold text-gray-700">판매가격</h3>
                    <label className="flex items-center gap-1 cursor-pointer text-xs">
                      <span className="text-gray-500">수정</span>
                      <input
                        type="checkbox"
                        checked={isPriceEditMode}
                        onChange={(e) => setIsPriceEditMode(e.target.checked)}
                        className="w-3.5 h-3.5 rounded border-gray-300"
                      />
                    </label>
                  </div>
                  <div className="overflow-hidden">
                    <table className="w-full divide-y divide-gray-200 text-sm table-fixed">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase w-16">플랫폼</th>
                          <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase">상품명</th>
                          <th className="px-2 py-1 text-right text-xs font-medium text-gray-500 uppercase w-24">가격</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {ROOM_TYPE_ORDER.map((type) => {
                          const prices = priceData.get(type);
                          if (!prices || prices.items.length === 0) return null;

                          return (
                            <React.Fragment key={type}>
                              {/* 객실타입 헤더 */}
                              <tr className="bg-gray-50">
                                <td colSpan={3} className="px-2 py-1">
                                  <RoomTypeBadge roomType={type} />
                                </td>
                              </tr>
                              {/* 가격 항목들 */}
                              {prices.items.map((item, idx) => (
                                <tr
                                  key={`${type}-${item.platform}-${idx}`}
                                  className={clsx(!item.saleOpen && 'text-gray-400 bg-gray-50')}
                                >
                                  <td className="px-2 py-1 whitespace-nowrap">
                                    <span className={clsx(
                                      'text-xs font-medium',
                                      item.platform === 'yanolja' ? 'text-red-500' : 'text-blue-500'
                                    )}>
                                      {item.platform === 'yanolja' ? '야놀자' : '여기어때'}
                                    </span>
                                  </td>
                                  <td className="px-2 py-1 text-xs truncate" title={item.name}>
                                    {item.name}
                                    {!item.saleOpen && <span className="ml-1 text-gray-400">(중지)</span>}
                                  </td>
                                  <td className="px-2 py-1 text-right whitespace-nowrap">
                                    <span className={clsx(
                                      'font-medium text-xs',
                                      item.price < 50000 && item.saleOpen && 'text-red-500'
                                    )}>
                                      {item.price.toLocaleString()}원
                                    </span>
                                    {isPriceEditMode && item.saleOpen && (
                                      <button
                                        onClick={() => setEditingPrice({ platform: item.platform, rulesetId: item.rulesetId, currentPrice: item.price, productName: item.name })}
                                        className="ml-2 text-gray-400 hover:text-gray-600"
                                      >
                                        <Edit2 className="w-3 h-3" />
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

            </div>
          )}
        </div>
      )}

      {/* Available Rooms Table */}
      <div className="card overflow-hidden p-0">
        <div className={clsx('px-4 py-3 border-b', isToday ? 'bg-blue-100' : 'bg-gray-100')}>
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-lg font-bold text-gray-900">
              예약되지 않은 객실 ({availableRooms?.length || 0})
            </h2>
            {Object.entries(availableByType).map(([type, count]) => (
              <RoomTypeBadge key={type} roomType={type} count={count} />
            ))}
          </div>
        </div>
        {isLoadingAvailable ? (
          <LoadingSpinner className="py-8" />
        ) : availableRooms && availableRooms.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">#</th>
                  <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">객실ID</th>
                  <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">호실</th>
                  <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">객실타입</th>
                  <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">청소</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {availableRooms.map((room: Room & { cleaningStatus?: string }, index: number) => (
                  <tr key={room.roomId} className={index % 2 === 1 ? 'bg-gray-50' : ''}>
                    <td className="px-2 py-2 text-center whitespace-nowrap">{index + 1}</td>
                    <td className="px-2 py-2 text-center whitespace-nowrap font-medium">{room.roomId.toUpperCase()}</td>
                    <td className="px-2 py-2 text-center whitespace-nowrap">{room.roomAddress?.toUpperCase() || '-'}</td>
                    <td className="px-2 py-2 text-center whitespace-nowrap">
                      <RoomTypeBadge roomType={room.roomType} />
                    </td>
                    <td className="px-2 py-2 text-center whitespace-nowrap text-xs">
                      {room.cleaningStatus || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">모든 객실이 예약되었습니다.</div>
        )}
      </div>

      {/* Cancelled Reservations Table */}
      <div className="card overflow-hidden p-0">
        <div className={clsx('px-4 py-3 border-b', isToday ? 'bg-blue-100' : 'bg-gray-100')}>
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-lg font-bold text-gray-900">
              취소된 객실 ({cancelledReservations?.length || 0})
            </h2>
            {Object.entries(cancelledByType).map(([type, count]) => (
              <RoomTypeBadge key={type} roomType={type} count={count} />
            ))}
          </div>
        </div>
        {isLoadingCancelled ? (
          <LoadingSpinner className="py-8" />
        ) : cancelledReservations && cancelledReservations.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">#</th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">예약번호</th>
                  <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">이름</th>
                  <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">박</th>
                  <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">차량</th>
                  <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">C/I Date</th>
                  <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">C/O Date</th>
                  <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">예약상태</th>
                  <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">C/I Time</th>
                  <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">C/O Time</th>
                  <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">Platform</th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">연락처</th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">체크인 특이사항</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {cancelledReservations.map((reservation: Reservation, index: number) => {
                  const checkinDateStr = reservation.checkinDate.split('T')[0];
                  const isCheckInDay = checkinDateStr === dateStr;

                  return (
                    <tr key={reservation.reservationId} className={index % 2 === 1 ? 'bg-gray-50' : ''}>
                      <td className="px-2 py-2 text-center whitespace-nowrap">{index + 1}</td>
                      <td className="px-2 py-2 whitespace-nowrap">
                        <span className={clsx(
                          'inline-block px-2 py-0.5 text-xs font-semibold rounded mr-1',
                          isCheckInDay ? 'bg-red-500 text-white' : 'bg-gray-500 text-white'
                        )}>
                          {isCheckInDay ? '취소(입실일)' : '취소(숙박중)'}
                        </span>
                        <Link
                          to={`/reservations/${reservation.reservationId}`}
                          className="text-blue-600 hover:underline text-xs"
                        >
                          {reservation.reservationId}
                        </Link>
                      </td>
                      <td className="px-2 py-2 text-center whitespace-nowrap">{reservation.guestName}</td>
                      <td className="px-2 py-2 text-center whitespace-nowrap">{reservation.nights}</td>
                      <td className="px-2 py-2 text-center whitespace-nowrap">
                        <RoomTypeBadge roomType={reservation.roomType} />
                      </td>
                      <td className="px-2 py-2 text-center text-xs whitespace-nowrap">
                        {reservation.guestParking && reservation.guestParking.length > 0 ? (
                          reservation.guestParking.map(p => p.carNumber).join(', ')
                        ) : '-'}
                      </td>
                      <td className="px-2 py-2 text-center whitespace-nowrap text-xs">
                        {format(new Date(reservation.checkinDate), 'MM/dd')}
                      </td>
                      <td className="px-2 py-2 text-center whitespace-nowrap text-xs">
                        {format(new Date(reservation.checkoutDate), 'MM/dd')}
                      </td>
                      <td className="px-2 py-2 text-center whitespace-nowrap">
                        <button
                          onClick={() => handleActivate(reservation.reservationId)}
                          className="px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600"
                          disabled={activateMutation.isPending}
                        >
                          예약활성화
                        </button>
                      </td>
                      <td className="px-2 py-2 text-center whitespace-nowrap text-xs">
                        {formatTime(reservation.checkinTime)}
                      </td>
                      <td className="px-2 py-2 text-center whitespace-nowrap text-xs">
                        {formatTime(reservation.checkoutTime)}
                      </td>
                      <td className="px-2 py-2 text-center whitespace-nowrap text-xs">{reservation.platform}</td>
                      <td className="px-2 py-2 whitespace-nowrap text-xs">{reservation.guestPhone || '-'}</td>
                      <td className="px-2 py-2 whitespace-nowrap text-xs">
                        {reservation.checkinMemo || '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">취소된 예약이 없습니다.</div>
        )}
      </div>

      {/* 수량 수정 모달 */}
      {editingQuantity && (
        <QuantityEditModal
          platform={editingQuantity.platform}
          roomType={editingQuantity.roomType}
          inventoryData={editingQuantity.platform === 'yanolja' ? yanoljaData : yeogiData}
          date={dateStr}
          onClose={() => setEditingQuantity(null)}
          onSave={async (updates) => {
            try {
              if (editingQuantity.platform === 'yanolja') {
                await platformApi.updateYanolja(dateStr, updates);
              } else {
                await platformApi.updateYeogi(dateStr, updates);
              }
              // 데이터 다시 로드
              const [yanolja, yeogi] = await Promise.all([
                platformApi.getYanoljaData(dateStr),
                platformApi.getYeogiData(dateStr),
              ]);
              setYanoljaData(yanolja);
              setYeogiData(yeogi);
              setEditingQuantity(null);
            } catch (error: any) {
              alert('수량 수정 실패: ' + (error.message || '알 수 없는 오류'));
            }
          }}
        />
      )}

      {/* 가격 수정 모달 */}
      {editingPrice && (
        <PriceEditModal
          platform={editingPrice.platform}
          rulesetId={editingPrice.rulesetId}
          currentPrice={editingPrice.currentPrice}
          productName={editingPrice.productName}
          date={dateStr}
          onClose={() => setEditingPrice(null)}
          onSave={async (newPrice) => {
            try {
              const updates: PlatformUpdate[] = [{ rulesetId: editingPrice.rulesetId, bookingRate: newPrice }];
              if (editingPrice.platform === 'yanolja') {
                await platformApi.updateYanolja(dateStr, updates);
              } else {
                await platformApi.updateYeogi(dateStr, updates);
              }
              // 데이터 다시 로드
              const [yanolja, yeogi] = await Promise.all([
                platformApi.getYanoljaData(dateStr),
                platformApi.getYeogiData(dateStr),
              ]);
              setYanoljaData(yanolja);
              setYeogiData(yeogi);
              setEditingPrice(null);
            } catch (error: any) {
              alert('가격 수정 실패: ' + (error.message || '알 수 없는 오류'));
            }
          }}
        />
      )}
    </div>
  );
}

// 가격 수정 모달 컴포넌트
function PriceEditModal({
  platform,
  rulesetId,
  currentPrice,
  productName,
  date,
  onClose,
  onSave,
}: {
  platform: 'yanolja' | 'yeogi';
  rulesetId: number;
  currentPrice: number;
  productName: string;
  date: string;
  onClose: () => void;
  onSave: (newPrice: number) => void;
}) {
  const [price, setPrice] = useState(currentPrice);
  const [isSaving, setIsSaving] = useState(false);

  // 천원 단위 증감
  const adjustPrice = (amount: number) => {
    setPrice((prev) => Math.max(0, prev + amount));
  };

  // 한글로 금액 표시
  const formatKoreanPrice = (amount: number): string => {
    if (amount >= 100000) {
      const man = Math.floor(amount / 10000);
      const rest = amount % 10000;
      if (rest === 0) {
        return `${man}만`;
      }
      return `${man}만 ${rest.toLocaleString()}`;
    } else if (amount >= 10000) {
      const man = Math.floor(amount / 10000);
      const rest = amount % 10000;
      if (rest === 0) {
        return `${man}만`;
      }
      return `${man}만 ${rest.toLocaleString()}`;
    }
    return amount.toLocaleString();
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(price);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl p-6 w-80" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-bold mb-2">
          가격 수정
          <span className={clsx(
            'ml-2 text-sm font-medium',
            platform === 'yanolja' ? 'text-red-500' : 'text-blue-500'
          )}>
            ({platform === 'yanolja' ? '야놀자' : '여기어때'})
          </span>
        </h3>

        <div className="mb-3 text-sm">
          <div className="text-gray-800 font-medium">{productName}</div>
          <div className="text-gray-500">날짜: {date}</div>
        </div>

        <div className="mb-4">
          <label className="block text-sm text-gray-600 mb-2">가격</label>
          <div className="flex items-center gap-2">
            <button
              onClick={() => adjustPrice(-10000)}
              className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300"
            >
              -1만
            </button>
            <button
              onClick={() => adjustPrice(-1000)}
              className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300"
            >
              -1천
            </button>
            <input
              type="text"
              value={price.toLocaleString()}
              onChange={(e) => {
                const num = parseInt(e.target.value.replace(/,/g, ''), 10);
                if (!isNaN(num)) setPrice(num);
              }}
              className="w-24 text-center border rounded px-2 py-1 text-lg font-bold"
            />
            <button
              onClick={() => adjustPrice(1000)}
              className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300"
            >
              +1천
            </button>
            <button
              onClick={() => adjustPrice(10000)}
              className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300"
            >
              +1만
            </button>
          </div>
          <div className="text-center mt-2 text-sm text-gray-600">
            {formatKoreanPrice(price)}원
          </div>
          {price < 50000 && (
            <div className="text-center mt-1 text-xs text-red-500">
              5만원 미만입니다. 확인해 주세요.
            </div>
          )}
        </div>

        <div className="flex gap-2 mt-6">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
            disabled={isSaving}
          >
            취소
          </button>
          <button
            onClick={handleSave}
            className="flex-1 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            disabled={isSaving}
          >
            {isSaving ? '저장중...' : '저장'}
          </button>
        </div>
      </div>
    </div>
  );
}

// 수량 수정 모달 컴포넌트
function QuantityEditModal({
  platform,
  roomType,
  inventoryData,
  date,
  onClose,
  onSave,
}: {
  platform: 'yanolja' | 'yeogi';
  roomType: string;
  inventoryData: PlatformData | null;
  date: string;
  onClose: () => void;
  onSave: (updates: PlatformUpdate[]) => void;
}) {
  const [quantities, setQuantities] = useState<{ rulesetId: number; name: string; current: number; value: number; saleOpen: boolean }[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  // 해당 객실 타입의 상품들 필터링
  useEffect(() => {
    if (!inventoryData?.data?.inventorySummaries) return;

    const filtered = inventoryData.data.inventorySummaries.filter(
      (item) => normalizeRoomType(item.roomtypeName) === roomType
    );

    setQuantities(
      filtered.map((item) => ({
        rulesetId: item.rulesetId,
        name: item.rulesetName.replace(item.roomtypeName + ' ', '').replace(/[()]/g, ''),
        current: item.reservableQuantity,
        value: item.reservableQuantity,
        saleOpen: item.saleOpen,
      }))
    );
  }, [inventoryData, roomType]);

  const updateQuantity = (rulesetId: number, delta: number) => {
    setQuantities((prev) =>
      prev.map((q) =>
        q.rulesetId === rulesetId ? { ...q, value: Math.max(0, q.value + delta) } : q
      )
    );
  };

  const setQuantityValue = (rulesetId: number, value: number) => {
    setQuantities((prev) =>
      prev.map((q) =>
        q.rulesetId === rulesetId ? { ...q, value: Math.max(0, value) } : q
      )
    );
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const updates: PlatformUpdate[] = quantities
        .filter((q) => q.saleOpen)
        .map((q) => ({ rulesetId: q.rulesetId, count: q.value }));
      await onSave(updates);
    } finally {
      setIsSaving(false);
    }
  };

  const roomTypeDisplay = roomType.charAt(0).toUpperCase() + roomType.slice(1);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl p-6 w-96 max-h-[80vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-bold mb-2">
          {roomTypeDisplay} - 수량 수정
          <span className={clsx(
            'ml-2 text-sm font-medium',
            platform === 'yanolja' ? 'text-red-500' : 'text-blue-500'
          )}>
            ({platform === 'yanolja' ? '야놀자' : '여기어때'})
          </span>
        </h3>

        <div className="text-sm text-gray-600 mb-3">날짜: {date}</div>

        <div className="flex-1 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="px-2 py-1 text-left">상품명</th>
                <th className="px-2 py-1 text-center w-12">현재</th>
                <th className="px-2 py-1 text-center w-28">수정</th>
              </tr>
            </thead>
            <tbody>
              {quantities.map((q) => (
                <tr
                  key={q.rulesetId}
                  className={clsx(!q.saleOpen && 'bg-gray-100 text-gray-400')}
                >
                  <td className="px-2 py-1 text-xs">
                    {q.name}
                    {!q.saleOpen && <span className="ml-1">(판매중지)</span>}
                  </td>
                  <td className="px-2 py-1 text-center">{q.current}</td>
                  <td className="px-2 py-1">
                    <div className="flex items-center justify-center gap-1">
                      <button
                        onClick={() => updateQuantity(q.rulesetId, -1)}
                        className="px-2 py-0.5 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50"
                        disabled={!q.saleOpen}
                      >
                        -
                      </button>
                      <input
                        type="number"
                        value={q.value}
                        onChange={(e) => setQuantityValue(q.rulesetId, parseInt(e.target.value) || 0)}
                        className="w-12 text-center border rounded px-1 py-0.5 disabled:bg-gray-100"
                        min={0}
                        disabled={!q.saleOpen}
                      />
                      <button
                        onClick={() => updateQuantity(q.rulesetId, 1)}
                        className="px-2 py-0.5 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50"
                        disabled={!q.saleOpen}
                      >
                        +
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex gap-2 mt-4 pt-4 border-t">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
            disabled={isSaving}
          >
            취소
          </button>
          <button
            onClick={handleSave}
            className="flex-1 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            disabled={isSaving}
          >
            {isSaving ? '저장중...' : '저장'}
          </button>
        </div>
      </div>
    </div>
  );
}
